import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useChemicalSearch from 'hooks/useChemicalSearch';
import useMix from 'hooks/useMix';
import useMixes from 'hooks/useMixes';
import usePageRoutes from 'hooks/usePageRoutes';

import { LIBRARY_TAB, MIXES_TAB } from 'pages/ChemicalsPage/constants';
import type { ChemicalsTab } from 'pages/ChemicalsPage/types';

import { DICAMBA_KEYWORD } from './constants';
import type { MixSummaryWithDicamba } from './types';

const getSortKey = (mix: MixSummaryWithDicamba): number => {
    if (!mix.updatedAt) {
        return 0;
    }

    const parsed = Date.parse(mix.updatedAt);

    return Number.isNaN(parsed) ? 0 : parsed;
};

const useData = () => {
    const { fetch, isLoading: isSearchLoading, results, search } = useChemicalSearch();
    const { fetch: fetchMixes, isLoading: isMixesLoading, mixes } = useMixes();
    const { fetchMix } = useMix();
    const { createMixPage } = usePageRoutes();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<ChemicalsTab>(MIXES_TAB);
    const [searchQuery, setSearchQuery] = useState('');
    const [productNamesById, setProductNamesById] = useState<Record<string, string[]>>({});

    useEffect(() => {
        fetchMixes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (mixes.length === 0) {
            return;
        }

        let cancelled = false;

        (async () => {
            const details = await Promise.all(mixes.map(mix => fetchMix({ id: mix.id })));

            if (cancelled) {
                return;
            }

            const nextMap: Record<string, string[]> = {};

            mixes.forEach((mix, index) => {
                const detail = details[index];

                if (detail) {
                    nextMap[mix.id] = detail.chemicals.map(chemical => chemical.labelName);
                }
            });

            setProductNamesById(nextMap);
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mixes]);

    const mixesWithDicamba = useMemo<MixSummaryWithDicamba[]>(() => {
        const enriched = mixes.map(mix => ({
            ...mix,
            hasDicamba: mix.name.toLowerCase().includes(DICAMBA_KEYWORD),
            productNames: productNamesById[mix.id] ?? []
        }));

        return enriched.sort((first, second) => getSortKey(second) - getSortKey(first));
    }, [mixes, productNamesById]);

    const isLibraryTab = activeTab === LIBRARY_TAB;
    const isMixesTab = activeTab === MIXES_TAB;

    const handleKeyDown = (key: string): void => {
        if (key === 'Enter') {
            handleSearch();
        }
    };

    const handleNewMix = () => {
        navigate(createMixPage);
    };

    const handleSearch = async (): Promise<void> => {
        if (searchQuery.trim().length < 2) {
            return;
        }

        await search({ query: searchQuery.trim() });
    };

    const handleNext = async () => await fetch().next();

    return {
        activeTab,
        handleKeyDown,
        handleNewMix,
        handleNext,
        handleSearch,
        isLibraryTab,
        isMixesLoading,
        isMixesTab,
        isSearchLoading,
        mixes: mixesWithDicamba,
        results,
        searchQuery,
        setActiveTab,
        setSearchQuery
    };
};

export default useData;
